import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const NotificationContext = createContext(null);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const pollingRef = useRef(null);

  // Check push support
  useEffect(() => {
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifsResp, countResp] = await Promise.all([
        axios.get(`${API_URL}/api/account/notifications`, { withCredentials: true }),
        axios.get(`${API_URL}/api/account/notifications/unread-count`, { withCredentials: true }),
      ]);
      setNotifications(notifsResp.data);
      setUnreadCount(countResp.data.count);
    } catch { }
  }, [user]);

  // Poll every 30s when user is logged in
  useEffect(() => {
    if (user) {
      fetchNotifications();
      pollingRef.current = setInterval(fetchNotifications, 30000);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [user, fetchNotifications]);

  // Mark one as read
  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/account/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/account/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { }
  };

  // Subscribe to push notifications
  const subscribePush = async () => {
    if (!pushSupported || !VAPID_PUBLIC_KEY) return false;
    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      const subJson = subscription.toJSON();
      await axios.post(`${API_URL}/api/account/push-subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }, { withCredentials: true });
      setPushEnabled(true);
      return true;
    } catch (err) {
      console.warn('Push subscription failed:', err);
      return false;
    }
  };

  // Unsubscribe
  const unsubscribePush = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }
      await axios.delete(`${API_URL}/api/account/push-unsubscribe`, { withCredentials: true });
      setPushEnabled(false);
    } catch { }
  };

  // Check if already subscribed on mount
  useEffect(() => {
    if (!pushSupported || !user) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setPushEnabled(!!sub);
        }
      } catch { }
    })();
  }, [pushSupported, user]);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, pushEnabled, pushSupported,
      fetchNotifications, markAsRead, markAllAsRead,
      subscribePush, unsubscribePush,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
